import { produce } from 'immer';
import { ADD_DIRECT_MESSAGE, CLEAR_DIRECT_MESSAGES_STATE, RESET_ACTIVE_CONVERSATION, SET_ACTIVE_DIRECT_MESSAGES_USER, SET_DIRECT_MESSAGE_LOG_FROM_INBOUND, SET_LAST_READ_DIRECT_MESSAGE, SET_RECENT_CONVERSATIONS_FROM_INBOUND } from '../actions/directMessagesActions';
const initialState = {
    hasFetchedRecentConversations: false,
    conversations: []
};
export function selectActiveConversation(state) {
    return state.directMessages.activeConversationPlayerId !== undefined
        ? state.directMessages.conversations.find((conversation) => conversation.playerIdName.id === state.directMessages.activeConversationPlayerId)
        : undefined;
}
/**
 * Creates a new, blank conversation if one doesn't already exist and adds it to the top of the list of conversations.
 * An existing conversation may be moved to the top of the list in some cases, such as when a new message arrives,
 * to indicate it is the most recently active conversation.
 */
function ensureConversationExists(draft, playerIdName, moveExistingConversationToTop) {
    const existingConversationIndex = draft.conversations.findIndex((conversation) => conversation.playerIdName.id === playerIdName.id);
    if (existingConversationIndex !== -1) {
        // conversation already exists
        if (moveExistingConversationToTop && existingConversationIndex !== 0) {
            // an action is being done that should move this conversation to the top, to indicate "most recently active"
            const [existingConversation] = draft.conversations.splice(existingConversationIndex, 1);
            draft.conversations.unshift(existingConversation);
            return draft.conversations[0];
        }
        return draft.conversations[existingConversationIndex];
    }
    // existing conversation was not found, add it at the top:
    const newConversation = {
        playerIdName,
        messages: []
    };
    draft.conversations.unshift(newConversation);
    return newConversation;
}
const directMessagesReducer = (state = initialState, action) => {
    return produce(state, (draft) => {
        switch (action.type) {
            case CLEAR_DIRECT_MESSAGES_STATE: {
                return initialState;
            }
            case SET_ACTIVE_DIRECT_MESSAGES_USER: {
                const playerIdName = action.payload;
                ensureConversationExists(draft, playerIdName, false);
                draft.activeConversationPlayerId = playerIdName.id;
                break;
            }
            case ADD_DIRECT_MESSAGE: {
                const { conversationPlayer, fromPlayer, messageId, messageHtml } = action.payload;
                const conversation = ensureConversationExists(draft, conversationPlayer, true);
                conversation.messages.push({
                    id: messageId,
                    fromPlayerId: fromPlayer?.id,
                    html: messageHtml
                });
                break;
            }
            case SET_DIRECT_MESSAGE_LOG_FROM_INBOUND: {
                const { conversationPlayer, messages } = action.payload;
                const existingConversation = draft.conversations.find((currentConversation) => currentConversation.playerIdName.id === conversationPlayer.id);
                if (existingConversation) {
                    existingConversation.messages = messages.map(({ id, fromPlayerId, messageHtml }) => ({
                        id,
                        fromPlayerId,
                        html: messageHtml
                    }));
                }
                break;
            }
            case SET_LAST_READ_DIRECT_MESSAGE: {
                const { playerId, lastUnreadMessageId } = action.payload;
                const conversation = draft.conversations.find((currentConversation) => currentConversation.playerIdName.id === playerId);
                if (conversation) {
                    conversation.lastReadMessageId = lastUnreadMessageId;
                }
                break;
            }
            case SET_RECENT_CONVERSATIONS_FROM_INBOUND: {
                const prevConversationPlayerIds = new Set(draft.conversations.map((conversation) => conversation.playerIdName.id));
                const newConversations = [...draft.conversations];
                action.payload.recentConversations.forEach((recentConversation) => {
                    if (!prevConversationPlayerIds.has(recentConversation.playerIdName.id)) {
                        newConversations.push({ playerIdName: recentConversation.playerIdName, messages: [] });
                    }
                });
                draft.conversations = newConversations;
                draft.hasFetchedRecentConversations = true;
                break;
            }
            case RESET_ACTIVE_CONVERSATION: {
                delete draft.activeConversationPlayerId;
                break;
            }
        }
    });
};
export default directMessagesReducer;
